/**
 * Finance / treasury / payments dashboard widgets.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBankAccounts, useBankTransactions } from '@/hooks/use-treasury';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { usePayments } from '@/hooks/use-payments';
import { Widget, HBarList, BigStat, MiniBars, fmtMoney, isoMonthStart } from './common';
import type { TopListRow } from './common';

// ---- Total bank balance across all accounts ----

export function TotalBankBalanceWidget() {
  const { t } = useTranslation();
  const { data: accounts, isLoading } = useBankAccounts();

  const stat = useMemo(() => {
    const active = (accounts ?? []).filter((a) => a.isActive);
    return {
      total: active.reduce((s, a) => s + (a.currentBalance ?? 0), 0),
      count: active.length,
    };
  }, [accounts]);

  return (
    <Widget
      title={t('dashboard.totalBankBalance')}
      description={t('dashboard.totalBankBalanceHelp')}
      isLoading={isLoading}
    >
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.accountsCount', { count: stat.count })}
        tone={stat.total < 0 ? 'negative' : 'neutral'}
      />
    </Widget>
  );
}

// ---- Bank balance breakdown (top accounts) ----

export function BankBalanceBreakdownWidget() {
  const { t } = useTranslation();
  const { data: accounts, isLoading } = useBankAccounts();

  const rows: TopListRow[] = useMemo(() => {
    return (accounts ?? [])
      .filter((a) => a.isActive)
      .slice()
      .sort((a, b) => (b.currentBalance ?? 0) - (a.currentBalance ?? 0))
      .slice(0, 5)
      .map((a) => ({
        id: a.id,
        label: a.name,
        sublabel: a.bankName ?? '',
        value: a.currentBalance ?? 0,
        unit: 'DT',
        decimals: 3,
      }));
  }, [accounts]);

  return (
    <Widget
      title={t('dashboard.bankBalanceBreakdown')}
      description={t('dashboard.bankBalanceBreakdownHelp')}
      isLoading={isLoading}
      isEmpty={rows.length === 0}
      emptyLabel={t('dashboard.noAccounts')}
    >
      <HBarList rows={rows} />
    </Widget>
  );
}

// ---- Outstanding receivables (unpaid sales invoices) ----

export function OutstandingReceivablesWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading: invLoading } = useSalesDocuments({ docType: 'invoice' });
  const { data: payments, isLoading: payLoading } = usePayments();

  const stat = useMemo(() => {
    // Sum allocations per invoice to compute remaining.
    const paidByDoc = new Map<string, number>();
    for (const p of payments ?? []) {
      const alloc = (p as unknown as { allocations?: Array<{ documentId: string; amount: number }> }).allocations ?? [];
      for (const a of alloc) {
        paidByDoc.set(a.documentId, (paidByDoc.get(a.documentId) ?? 0) + a.amount);
      }
    }
    let unpaidTotal = 0;
    let unpaidCount = 0;
    for (const inv of invoices ?? []) {
      const paid = paidByDoc.get(inv.id) ?? 0;
      const remaining = Math.max(0, inv.netToPay - paid);
      if (remaining > 0.001) {
        unpaidTotal += remaining;
        unpaidCount++;
      }
    }
    return { total: unpaidTotal, count: unpaidCount };
  }, [invoices, payments]);

  return (
    <Widget
      title={t('dashboard.outstandingReceivables')}
      description={t('dashboard.outstandingReceivablesHelp')}
      isLoading={invLoading || payLoading}
    >
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.invoicesCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'positive'}
      />
    </Widget>
  );
}

// ---- Outstanding payables (unpaid purchase invoices) ----

export function OutstandingPayablesWidget() {
  const { t } = useTranslation();
  const { data: invoices, isLoading: invLoading } = usePurchaseDocuments({ docType: 'invoice' });
  const { data: payments, isLoading: payLoading } = usePayments();

  const stat = useMemo(() => {
    const paidByDoc = new Map<string, number>();
    for (const p of payments ?? []) {
      const alloc = (p as unknown as { allocations?: Array<{ documentId: string; amount: number }> }).allocations ?? [];
      for (const a of alloc) {
        paidByDoc.set(a.documentId, (paidByDoc.get(a.documentId) ?? 0) + a.amount);
      }
    }
    let unpaidTotal = 0;
    let unpaidCount = 0;
    for (const inv of invoices ?? []) {
      const net = (inv as unknown as { netToPay?: number }).netToPay ?? inv.totalTtc;
      const paid = paidByDoc.get(inv.id) ?? 0;
      const remaining = Math.max(0, net - paid);
      if (remaining > 0.001) {
        unpaidTotal += remaining;
        unpaidCount++;
      }
    }
    return { total: unpaidTotal, count: unpaidCount };
  }, [invoices, payments]);

  return (
    <Widget
      title={t('dashboard.outstandingPayables')}
      description={t('dashboard.outstandingPayablesHelp')}
      isLoading={invLoading || payLoading}
    >
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.invoicesCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}

// ---- Cashflow this month (transactions in vs out) ----

export function CashflowThisMonthWidget() {
  const { t } = useTranslation();
  const { data: transactions, isLoading } = useBankTransactions();

  const stat = useMemo(() => {
    const now = new Date();
    const monthStart = isoMonthStart(now.getFullYear(), now.getMonth());
    let inTotal = 0;
    let outTotal = 0;
    for (const tx of transactions ?? []) {
      if (tx.transactionDate < monthStart) continue;
      if (tx.direction === 'in') inTotal += tx.amount;
      else if (tx.direction === 'out') outTotal += tx.amount;
    }
    return { inTotal, outTotal, net: inTotal - outTotal };
  }, [transactions]);

  return (
    <Widget title={t('dashboard.cashflowThisMonth')} description={t('dashboard.cashflowHelp')} isLoading={isLoading}>
      <MiniBars
        bars={[
          { label: t('dashboard.inflows'), value: stat.inTotal, color: 'oklch(0.72 0.16 155)' /* emerald */ },
          { label: t('dashboard.outflows'), value: stat.outTotal, color: 'oklch(0.65 0.22 25)' /* rose */ },
        ]}
      />
      <div className="mt-2 pt-2 border-t flex items-baseline justify-between">
        <span className="text-xs text-muted-foreground">{t('dashboard.netCashflow')}</span>
        <span className={`text-lg font-semibold tabular-nums ${stat.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {stat.net >= 0 ? '+' : ''}{fmtMoney(stat.net, 3)}
          <span className="text-xs text-muted-foreground ms-1 font-normal">DT</span>
        </span>
      </div>
    </Widget>
  );
}
